import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import axios from 'axios'
import { API_URL } from '@/config/constants'

interface TourState {
  isActive: boolean
  currentStep: number
  hasCompleted: boolean
  isFirstTime?: boolean
}

interface AdminStore {
  // Tour states
  globalTour: TourState
  datastorageTour: TourState
  studentsTour: TourState
  classTour: TourState
  gradeTour: TourState
  classroomTour: TourState
  subjectTour: TourState
  timetableTour: TourState
  databaseOverviewTour: TourState
  timetableOverviewTour: TourState
  attendanceTour: TourState
  sessionManagementTour: TourState
  sessionGeneratorTour: TourState
  teacherAbsenceTour: TourState
  viewTour: TourState
  attestationTour: TourState
  activeComponent: string | null

  // Basic Timetable Tour State
  basicTimetableTour: {
    isActive: boolean
    currentStep: number
  }

  // Navigation Tour State
  navTour: {
    isActive: boolean
    currentStep: number
  }

  // Tour actions
  startGlobalTour: () => void
  nextGlobalStep: () => void
  completeGlobalTour: () => void
  resetGlobalTour: () => void
  setGlobalStep: (step: number) => void

  startDatastorageTour: () => void
  nextDatastorageStep: () => void
  completeDatastorageTour: () => void
  resetDatastorageTour: () => void
  setDatastorageStep: (step: number) => void

  startStudentsTour: () => void
  nextStudentsStep: () => void
  completeStudentsTour: () => void
  resetStudentsTour: () => void
  setStudentsStep: (step: number) => void

  startClassTour: () => void
  nextClassStep: () => void
  completeClassTour: () => void
  resetClassTour: () => void
  setClassStep: (step: number) => void

  startGradeTour: () => void
  nextGradeStep: () => void
  completeGradeTour: () => void
  resetGradeTour: () => void
  setGradeStep: (step: number) => void

  startClassroomTour: () => void
  nextClassroomStep: () => void
  completeClassroomTour: () => void
  resetClassroomTour: () => void
  setClassroomStep: (step: number) => void

  startSubjectTour: () => void
  nextSubjectStep: () => void
  completeSubjectTour: () => void
  resetSubjectTour: () => void
  setSubjectStep: (step: number) => void

  startTimetableTour: () => void
  nextTimetableStep: () => void
  completeTimetableTour: () => void
  resetTimetableTour: () => void
  setTimetableStep: (step: number) => void

  startDatabaseOverviewTour: () => void
  nextDatabaseOverviewStep: () => void
  completeDatabaseOverviewTour: () => void
  resetDatabaseOverviewTour: () => void
  setDatabaseOverviewStep: (step: number) => void

  startTimetableOverviewTour: () => void
  nextTimetableOverviewStep: () => void
  completeTimetableOverviewTour: () => void
  resetTimetableOverviewTour: () => void
  setTimetableOverviewStep: (step: number) => void

  startViewTour: () => void
  completeViewTour: () => void
  setViewStep: (step: number) => void
  nextViewStep: () => void
  resetViewTour: () => void

  startSessionManagementTour: () => void
  nextSessionManagementStep: () => void
  completeSessionManagementTour: () => void
  resetSessionManagementTour: () => void
  setSessionManagementStep: (step: number) => void

  startSessionGeneratorTour: () => void
  completeSessionGeneratorTour: () => void
  setSessionGeneratorStep: (step: number) => void
  nextSessionGeneratorStep: () => void
  resetSessionGeneratorTour: () => void

  statusOverviewTour: TourState
  startStatusOverviewTour: () => void
  completeStatusOverviewTour: () => void
  setStatusOverviewStep: (step: number) => void
  nextStatusOverviewStep: () => void
  resetStatusOverviewTour: () => void

  studentSessionsTour: TourState
  startStudentSessionsTour: () => void
  completeStudentSessionsTour: () => void
  setStudentSessionsStep: (step: number) => void
  nextStudentSessionsStep: () => void
  resetStudentSessionsTour: () => void

  startTeacherAbsenceTour: () => void
  completeTeacherAbsenceTour: () => void
  setTeacherAbsenceStep: (step: number) => void

  // Attendance Tour actions
  startAttendanceTour: () => void
  nextAttendanceStep: () => void
  completeAttendanceTour: () => void
  resetAttendanceTour: () => void
  setAttendanceStep: (step: number) => void

  startAttestationTour: () => void
  nextAttestationStep: () => void
  completeAttestationTour: () => void
  resetAttestationTour: () => void
  setAttestationStep: (step: number) => void

  // Basic Timetable Tour actions
  startBasicTimetableTour: () => void
  completeBasicTimetableTour: () => void
  setBasicTimetableStep: (step: number) => void
  nextBasicTimetableStep: () => void

  // Navigation Tour actions
  startNavTour: () => void
  completeNavTour: () => void
  setNavStep: (step: number) => void
  nextNavStep: () => void

  setActiveComponent: (component: string | null) => void
  resetAllToursToFirstTime: () => void
}

// Initial state
const initialTourState: TourState = {
  isActive: false,
  currentStep: 0,
  hasCompleted: false,
  isFirstTime: true // Add this to track first time
}

// API function to update onboarding status
const updateOnboarding = async (status: boolean) => {
  try {
    await axios.patch(
      `${API_URL}/user-controller/update-onboarding`,
      { userOnboarding: status },
      {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true, // Send HTTP-only cookies
      }
    )
// console.log✅ Sent userOnboarding =', status)
    // Dispatch event to trigger tour status refresh in useTourManager
    window.dispatchEvent(new CustomEvent('tour-status-change'))
  } catch (err) {
    // console.error('❌ Failed to update onboarding status:', err)
  }
}

// API function to get onboarding flag
export const getUserOnboardingStatus = async (): Promise<boolean | null> => {
  try {
    const response = await axios.get(`${API_URL}/user-controller/session`, {
      withCredentials: true, // Send HTTP-only cookies
    })
    const userOnboarding = response.data?.userOnboarding
// console.log🟢 userOnboarding:', userOnboarding)
    return userOnboarding
  } catch (err) {
    // console.error('❌ Failed to fetch onboarding status:', err)
    return null
  }
}

// Helper function to check if all tours are completed
const checkIfAllToursCompleted = (state: AdminStore) => {
  const allCompleted = 
    state.globalTour.hasCompleted &&
    state.datastorageTour.hasCompleted &&
    state.studentsTour.hasCompleted &&
    state.classTour.hasCompleted &&
    state.gradeTour.hasCompleted &&
    state.classroomTour.hasCompleted &&
    state.subjectTour.hasCompleted &&
    state.timetableTour.hasCompleted &&
    // state.databaseOverviewTour.hasCompleted &&
    state.timetableOverviewTour.hasCompleted &&
    state.attendanceTour.hasCompleted &&
    state.sessionManagementTour.hasCompleted &&
    state.sessionGeneratorTour.hasCompleted &&
    state.teacherAbsenceTour.hasCompleted &&
    state.viewTour.hasCompleted

  if (allCompleted) {
// console.log🎉 All admin tours completed! Updating onboarding to false')
    updateOnboarding(false)
  }
}



// Init function - tours are now managed by useTourManager hook
export const initAdminTourFromServer = async () => {
  // Tours are now managed by useTourManager hook
  // No need for manual initialization
}

// Zustand Store
export const useAdminStore = create<AdminStore>()(
  persist(
    (set) => ({
      // Tour states
      databaseOverviewTour: { ...initialTourState },
      globalTour: { ...initialTourState },
      datastorageTour: { ...initialTourState },
      studentsTour: { ...initialTourState },
      classTour: { ...initialTourState },
      gradeTour: { ...initialTourState },
      classroomTour: { ...initialTourState },
      subjectTour: { ...initialTourState },
      timetableTour: { ...initialTourState },
      timetableOverviewTour: { ...initialTourState },
      attendanceTour: { ...initialTourState },
      sessionManagementTour: { ...initialTourState },
      sessionGeneratorTour: { ...initialTourState },
      statusOverviewTour: { ...initialTourState },
      studentSessionsTour: { ...initialTourState },
      teacherAbsenceTour: { ...initialTourState },
      viewTour: { ...initialTourState },
      attestationTour: { ...initialTourState },
      activeComponent: null,

      // Basic Timetable Tour State
      basicTimetableTour: {
        isActive: false,
        currentStep: 0,
      },

      // Navigation Tour State
      navTour: {
        isActive: false,
        currentStep: 0,
      },

      // Tour actions
      startGlobalTour: () => {
        set((state) => ({
          globalTour: {
            ...state.globalTour,
            isActive: true,
            currentStep: 0,
          },
        }))
      },
      nextGlobalStep: () =>
        set((state) => ({
          globalTour: {
            ...state.globalTour,
            currentStep: state.globalTour.currentStep + 1,
          },
        })),
      completeGlobalTour: () => {
        set((state) => ({
          globalTour: {
            ...state.globalTour,
            isActive: false,
            hasCompleted: true,
          },
        }))
        // Check if all tours are completed after updating state
        setTimeout(() => checkIfAllToursCompleted(useAdminStore.getState()), 0)
      },
      resetGlobalTour: () => {
        set(() => ({
          globalTour: { ...initialTourState },
        }))
      },
      setGlobalStep: (step: number) =>
        set((state) => ({
          globalTour: {
            ...state.globalTour,
            currentStep: step,
          },
        })),

      startDatastorageTour: () =>
        set((state) => ({
          datastorageTour: {
            ...state.datastorageTour,
            isActive: true,
            currentStep: 0,
          },
        })),
      nextDatastorageStep: () =>
        set((state) => ({
          datastorageTour: {
            ...state.datastorageTour,
            currentStep: state.datastorageTour.currentStep + 1,
          },
        })),
      completeDatastorageTour: () => {
        set((state) => ({
          datastorageTour: {
            ...state.datastorageTour,
            isActive: false,
            hasCompleted: true,
          },
        }))
        // Check if all tours are completed after updating state
        setTimeout(() => checkIfAllToursCompleted(useAdminStore.getState()), 0)
      },
      resetDatastorageTour: () =>
        set(() => ({
          datastorageTour: { ...initialTourState },
        })),
      setDatastorageStep: (step: number) =>
        set((state) => ({
          datastorageTour: {
            ...state.datastorageTour,
            currentStep: step,
          },
        })),

      startStudentsTour: () => {
        updateOnboarding(false);
        set((state) => ({
          studentsTour: {
            ...state.studentsTour,
            isActive: true,
            currentStep: 0,
          },
        }));
      },
      nextStudentsStep: () =>
        set((state) => ({
          studentsTour: {
            ...state.studentsTour,
            currentStep: state.studentsTour.currentStep + 1,
          },
        })),
      completeStudentsTour: () => {
        updateOnboarding(true);
        set((state) => ({
          studentsTour: {
            ...state.studentsTour,
            isActive: false,
            hasCompleted: true,
          },
        }))
        // Check if all tours are completed after updating state
        setTimeout(() => checkIfAllToursCompleted(useAdminStore.getState()), 0)
      },
      resetStudentsTour: () =>
        set(() => ({
          studentsTour: { ...initialTourState },
        })),
      setStudentsStep: (step: number) =>
        set((state) => ({
          studentsTour: {
            ...state.studentsTour,
            currentStep: step,
          },
        })),

      startClassTour: () =>
        set((state) => ({
          classTour: {
            ...state.classTour,
            isActive: true,
            currentStep: 0,
          },
        })),
      nextClassStep: () =>
        set((state) => ({
          classTour: {
            ...state.classTour,
            currentStep: state.classTour.currentStep + 1,
          },
        })),
      completeClassTour: () => {
        set((state) => ({
          classTour: {
            ...state.classTour,
            isActive: false,
            hasCompleted: true,
          },
        }))
        // Check if all tours are completed after updating state
        setTimeout(() => checkIfAllToursCompleted(useAdminStore.getState()), 0)
      },
      resetClassTour: () =>
        set(() => ({
          classTour: { ...initialTourState },
        })),
      setClassStep: (step: number) =>
        set((state) => ({
          classTour: {
            ...state.classTour,
            currentStep: step,
          },
        })),

      startGradeTour: () =>
        set((state) => ({
          gradeTour: {
            ...state.gradeTour,
            isActive: true,
            currentStep: 0,
          },
        })),
      nextGradeStep: () =>
        set((state) => ({
          gradeTour: {
            ...state.gradeTour,
            currentStep: state.gradeTour.currentStep + 1,
          },
        })),
      completeGradeTour: () => {
        updateOnboarding(false);
        set((state) => ({
          gradeTour: {
            ...state.gradeTour,
            isActive: false,
            hasCompleted: true,
          },
        }))
      },
      resetGradeTour: () =>
        set(() => ({
          gradeTour: { ...initialTourState },
        })),
      setGradeStep: (step: number) =>
        set((state) => ({
          gradeTour: {
            ...state.gradeTour,
            currentStep: step,
          },
        })),

      startClassroomTour: () =>
        set((state) => ({
          classroomTour: {
            ...state.classroomTour,
            isActive: true,
            currentStep: 0,
          },
        })),
      nextClassroomStep: () =>
        set((state) => ({
          classroomTour: {
            ...state.classroomTour,
            currentStep: state.classroomTour.currentStep + 1,
          },
        })),
      completeClassroomTour: () => {
        updateOnboarding(false);
        set((state) => ({
          classroomTour: {
            ...state.classroomTour,
            isActive: false,
            hasCompleted: true,
          },
        }))
      },
      resetClassroomTour: () =>
        set(() => ({
          classroomTour: { ...initialTourState },
        })),
      setClassroomStep: (step: number) =>
        set((state) => ({
          classroomTour: {
            ...state.classroomTour,
            currentStep: step,
          },
        })),

      startSubjectTour: () =>
        set((state) => ({
          subjectTour: {
            ...state.subjectTour,
            isActive: true,
            currentStep: 0,
          },
        })),
      nextSubjectStep: () =>
        set((state) => ({
          subjectTour: {
            ...state.subjectTour,
            currentStep: state.subjectTour.currentStep + 1,
          },
        })),
      completeSubjectTour: () => {
        set((state) => ({
          subjectTour: {
            ...state.subjectTour,
            isActive: false,
            hasCompleted: true,
          },
        }))
        // Check if all tours are completed after updating state
        setTimeout(() => checkIfAllToursCompleted(useAdminStore.getState()), 0)
      },
      resetSubjectTour: () =>
        set(() => ({
          subjectTour: { ...initialTourState },
        })),
      setSubjectStep: (step: number) =>
        set((state) => ({
          subjectTour: {
            ...state.subjectTour,
            currentStep: step,
          },
        })),

      startTimetableTour: () => {
        updateOnboarding(false);
        set((state) => ({
          timetableTour: {
            ...state.timetableTour,
            isActive: true,
            currentStep: 0,
          },
        }));
      },
      nextTimetableStep: () =>
        set((state) => ({
          timetableTour: {
            ...state.timetableTour,
            currentStep: state.timetableTour.currentStep + 1,
          },
        })),
      completeTimetableTour: () => {
        updateOnboarding(true);
        set((state) => ({
          timetableTour: {
            ...state.timetableTour,
            isActive: false,
            hasCompleted: true,
          },
        }))
        // Check if all tours are completed after updating state
        setTimeout(() => checkIfAllToursCompleted(useAdminStore.getState()), 0)
      },
      resetTimetableTour: () =>
        set(() => ({
          timetableTour: { ...initialTourState },
        })),
      setTimetableStep: (step: number) =>
        set((state) => ({
          timetableTour: {
            ...state.timetableTour,
            currentStep: step,
          },
        })),

      startDatabaseOverviewTour: () =>
        set((state) => ({
          databaseOverviewTour: {
            ...state.databaseOverviewTour,
            isActive: true,
            currentStep: 0,
          },
        })),
      nextDatabaseOverviewStep: () =>
        set((state) => ({
          databaseOverviewTour: {
            ...state.databaseOverviewTour,
            currentStep: state.databaseOverviewTour.currentStep + 1,
          },
        })),
      completeDatabaseOverviewTour: () => {
        set((state) => ({
          databaseOverviewTour: {
            ...state.databaseOverviewTour,
            isActive: false,
            hasCompleted: true,
          },
        }))
        // Check if all tours are completed after updating state
        setTimeout(() => checkIfAllToursCompleted(useAdminStore.getState()), 0)
      },
      resetDatabaseOverviewTour: () =>
        set(() => ({
          databaseOverviewTour: { ...initialTourState },
        })),
      setDatabaseOverviewStep: (step: number) =>
        set((state) => ({
          databaseOverviewTour: {
            ...state.databaseOverviewTour,
            currentStep: step,
          },
        })),

      // Basic Timetable Tour
      startBasicTimetableTour: () =>
        set(() => ({
          basicTimetableTour: {
            isActive: true,
            currentStep: 0,
          },
        })),
      completeBasicTimetableTour: () => {
        updateOnboarding(false);
        set(() => ({
          basicTimetableTour: {
            isActive: false,
            currentStep: 0,
          },
        }))
      },
      setBasicTimetableStep: (step: number) =>
        set((state) => ({
          basicTimetableTour: {
            ...state.basicTimetableTour,
            currentStep: step,
          },
        })),
      nextBasicTimetableStep: () =>
        set((state) => ({
          basicTimetableTour: {
            ...state.basicTimetableTour,
            currentStep: state.basicTimetableTour.currentStep + 1,
          },
        })),

      // Navigation Tour
      startNavTour: () =>
        set(() => ({
          navTour: {
            isActive: true,
            currentStep: 0,
          },
        })),
      completeNavTour: () => {
        updateOnboarding(false);
        set(() => ({
          navTour: {
            isActive: false,
            currentStep: 0,
          },
        }))
      },
      setNavStep: (step: number) =>
        set((state) => ({
          navTour: {
            ...state.navTour,
            currentStep: step,
          },
        })),
      nextNavStep: () =>
        set((state) => ({
          navTour: {
            ...state.navTour,
            currentStep: state.navTour.currentStep + 1,
          },
        })),

      // View Tour functions
      startViewTour: () =>
        set((state) => ({
          viewTour: {
            ...state.viewTour,
            isActive: true,
            currentStep: 0,
          },
        })),
      completeViewTour: () => {
        set((state) => ({
          viewTour: {
            ...state.viewTour,
            isActive: false,
            hasCompleted: true,
          },
        }))
        // Check if all tours are completed after updating state
        setTimeout(() => checkIfAllToursCompleted(useAdminStore.getState()), 0)
      },
      setViewStep: (step: number) =>
        set((state) => ({
          viewTour: {
            ...state.viewTour,
            currentStep: step,
          },
        })),
      nextViewStep: () =>
        set((state) => ({
          viewTour: {
            ...state.viewTour,
            currentStep: state.viewTour.currentStep + 1,
          },
        })),
      resetViewTour: () =>
        set(() => ({
          viewTour: { ...initialTourState },
        })),

      // Timetable Overview Tour functions
      startTimetableOverviewTour: () =>
        set((state) => ({
          timetableOverviewTour: {
            ...state.timetableOverviewTour,
            isActive: true,
            currentStep: 0,
          },
        })),
      completeTimetableOverviewTour: () => {
        set((state) => ({
          timetableOverviewTour: {
            ...state.timetableOverviewTour,
            isActive: false,
            hasCompleted: true,
          },
        }))
        // Check if all tours are completed after updating state
        setTimeout(() => checkIfAllToursCompleted(useAdminStore.getState()), 0)
      },
      setTimetableOverviewStep: (step: number) =>
        set((state) => ({
          timetableOverviewTour: {
            ...state.timetableOverviewTour,
            currentStep: step,
          },
        })),
      nextTimetableOverviewStep: () =>
        set((state) => ({
          timetableOverviewTour: {
            ...state.timetableOverviewTour,
            currentStep: state.timetableOverviewTour.currentStep + 1,
          },
        })),
      resetTimetableOverviewTour: () =>
        set(() => ({
          timetableOverviewTour: { ...initialTourState },
        })),

      startSessionManagementTour: () =>
        set((state) => ({
          sessionManagementTour: {
            ...state.sessionManagementTour,
            isActive: true,
            currentStep: 0,
          },
        })),
      nextSessionManagementStep: () =>
        set((state) => ({
          sessionManagementTour: {
            ...state.sessionManagementTour,
            currentStep: state.sessionManagementTour.currentStep + 1,
          },
        })),
      completeSessionManagementTour: () => {
        set((state) => ({
          sessionManagementTour: {
            ...state.sessionManagementTour,
            isActive: false,
            hasCompleted: true,
          },
        }))
        // Check if all tours are completed after updating state
        setTimeout(() => checkIfAllToursCompleted(useAdminStore.getState()), 0)
      },
      resetSessionManagementTour: () =>
        set(() => ({
          sessionManagementTour: { ...initialTourState },
        })),
      setSessionManagementStep: (step: number) =>
        set((state) => ({
          sessionManagementTour: {
            ...state.sessionManagementTour,
            currentStep: step,
          },
        })),

      startSessionGeneratorTour: () => {
        updateOnboarding(false);
        set((state) => ({
          sessionGeneratorTour: {
            ...state.sessionGeneratorTour,
            isActive: true,
            currentStep: 0,
          },
        }));
      },
      completeSessionGeneratorTour: () => {
        updateOnboarding(true);
        set((state) => ({
          sessionGeneratorTour: {
            ...state.sessionGeneratorTour,
            isActive: false,
            hasCompleted: true,
          },
        }));
        // Check if all tours are completed after updating state
        setTimeout(() => checkIfAllToursCompleted(useAdminStore.getState()), 0)
      },
      setSessionGeneratorStep: (step: number) =>
        set((state) => ({
          sessionGeneratorTour: {
            ...state.sessionGeneratorTour,
            currentStep: step,
          },
        })),
      nextSessionGeneratorStep: () =>
        set((state) => ({
          sessionGeneratorTour: {
            ...state.sessionGeneratorTour,
            currentStep: state.sessionGeneratorTour.currentStep + 1,
          },
        })),
      resetSessionGeneratorTour: () =>
        set(() => ({
          sessionGeneratorTour: { ...initialTourState },
        })),

      // Status Overview Tour
      startStatusOverviewTour: () => {
        updateOnboarding(false);
        set((state) => ({
          statusOverviewTour: {
            ...state.statusOverviewTour,
            isActive: true,
            currentStep: 0,
          },
        }));
      },
      completeStatusOverviewTour: () => {
        updateOnboarding(true);
        set((state) => ({
          statusOverviewTour: {
            ...state.statusOverviewTour,
            isActive: false,
            hasCompleted: true,
          },
        }));
      },
      setStatusOverviewStep: (step: number) =>
        set((state) => ({
          statusOverviewTour: {
            ...state.statusOverviewTour,
            currentStep: step,
          },
        })),
      nextStatusOverviewStep: () =>
        set((state) => ({
          statusOverviewTour: {
            ...state.statusOverviewTour,
            currentStep: state.statusOverviewTour.currentStep + 1,
          },
        })),
      resetStatusOverviewTour: () =>
        set(() => ({
          statusOverviewTour: { ...initialTourState },
        })),

      // Student Sessions Tour
      startStudentSessionsTour: () => {
        updateOnboarding(false);
        set((state) => ({
          studentSessionsTour: {
            ...state.studentSessionsTour,
            isActive: true,
            currentStep: 0,
          },
        }));
      },
      completeStudentSessionsTour: () => {
        updateOnboarding(true);
        set((state) => ({
          studentSessionsTour: {
            ...state.studentSessionsTour,
            isActive: false,
            hasCompleted: true,
          },
        }));
      },
      setStudentSessionsStep: (step: number) =>
        set((state) => ({
          studentSessionsTour: {
            ...state.studentSessionsTour,
            currentStep: step,
          },
        })),
      nextStudentSessionsStep: () =>
        set((state) => ({
          studentSessionsTour: {
            ...state.studentSessionsTour,
            currentStep: state.studentSessionsTour.currentStep + 1,
          },
        })),
      resetStudentSessionsTour: () =>
        set(() => ({
          studentSessionsTour: { ...initialTourState },
        })),

      startTeacherAbsenceTour: () =>
        set((state) => ({
          teacherAbsenceTour: {
            ...state.teacherAbsenceTour,
            isActive: true,
            currentStep: 0,
          },
        })),
      completeTeacherAbsenceTour: () => {
        updateOnboarding(false);
        set((state) => ({
          teacherAbsenceTour: {
            ...state.teacherAbsenceTour,
            isActive: false,
            hasCompleted: true,
          },
        }));
      },
      setTeacherAbsenceStep: (step: number) =>
        set((state) => ({
          teacherAbsenceTour: {
            ...state.teacherAbsenceTour,
            currentStep: step,
          },
        })),

      // Attendance Tour
      startAttendanceTour: () => {
        updateOnboarding(false);
        set((state) => ({
          attendanceTour: {
            ...state.attendanceTour,
            isActive: true,
            currentStep: 0,
          },
        }));
      },
      nextAttendanceStep: () =>
        set((state) => ({
          attendanceTour: {
            ...state.attendanceTour,
            currentStep: state.attendanceTour.currentStep + 1,
          },
        })),
      completeAttendanceTour: () => {
        updateOnboarding(true);
        set((state) => ({
          attendanceTour: {
            ...state.attendanceTour,
            isActive: false,
            hasCompleted: true,
          },
        }));
      },
      resetAttendanceTour: () =>
        set(() => ({
          attendanceTour: { ...initialTourState },
        })),
      setAttendanceStep: (step: number) =>
        set((state) => ({
          attendanceTour: {
            ...state.attendanceTour,
            currentStep: step,
          },
        })),

      startAttestationTour: () => {
        updateOnboarding(false);
        set((state) => ({
          attestationTour: {
            ...state.attestationTour,
            isActive: true,
            currentStep: 0,
          },
        }));
      },
      nextAttestationStep: () =>
        set((state) => ({
          attestationTour: {
            ...state.attestationTour,
            currentStep: state.attestationTour.currentStep + 1,
          },
        })),
      completeAttestationTour: () => {
        updateOnboarding(true);
        set((state) => ({
          attestationTour: {
            ...state.attestationTour,
            isActive: false,
            hasCompleted: true,
          },
        }));
      },
      resetAttestationTour: () =>
        set(() => ({
          attestationTour: { ...initialTourState },
        })),
      setAttestationStep: (step: number) =>
        set((state) => ({
          attestationTour: {
            ...state.attestationTour,
            currentStep: step,
          },
        })),

      setActiveComponent: (component: string | null) =>
        set(() => ({
          activeComponent: component,
        })),

      // Add new function to reset everything to first time
      resetAllToursToFirstTime: () => {
    // console.logresetAllToursToFirstTime: Starting reset...')
        
        // Clear all localStorage items related to tours
        localStorage.removeItem('isFirstTimeUser')
        localStorage.removeItem('userOnboarding')
        localStorage.removeItem('tour_completed_establishment')
        localStorage.removeItem('tour_completed_admin_timetable')
        localStorage.removeItem('tour_skipped_establishment')
        localStorage.removeItem('tour_skipped_admin_timetable')
        localStorage.removeItem('trigger_admin_timetable_tour')
        localStorage.removeItem('tour_completed_super_admin')
        localStorage.removeItem('tour_completed_database')
        localStorage.removeItem('tour_completed_profile')
        localStorage.removeItem('super_admin_tour_active')
        localStorage.removeItem('tour_step_index')

        // Update server onboarding status - set to false to show tours
        updateOnboarding(false)

        // Reset all tour states to initial state, but activate multiple tours
        set(() => ({
          globalTour: { 
            isActive: true, 
            currentStep: 0, 
            hasCompleted: false,
            isFirstTime: true
          },
          datastorageTour: { 
            isActive: true, 
            currentStep: 0, 
            hasCompleted: false,
            isFirstTime: true
          },
          studentsTour: { 
            isActive: true, 
            currentStep: 0, 
            hasCompleted: false,
            isFirstTime: true
          },
          timetableTour: { 
            isActive: true, 
            currentStep: 0, 
            hasCompleted: false,
            isFirstTime: true
          },
          databaseOverviewTour: { 
            isActive: true, 
            currentStep: 0, 
            hasCompleted: false,
            isFirstTime: true
          },
          timetableOverviewTour: { 
            isActive: true, 
            currentStep: 0, 
            hasCompleted: false,
            isFirstTime: true
          },
          attendanceTour: { 
            isActive: true, 
            currentStep: 0, 
            hasCompleted: false,
            isFirstTime: true
          },
          sessionManagementTour: { 
            isActive: true, 
            currentStep: 0, 
            hasCompleted: false,
            isFirstTime: true
          },
          sessionGeneratorTour: { 
            isActive: true, 
            currentStep: 0, 
            hasCompleted: false,
            isFirstTime: true
          },
          teacherAbsenceTour: { 
            isActive: true, 
            currentStep: 0, 
            hasCompleted: false,
            isFirstTime: true
          },
          statusOverviewTour: { 
            isActive: true, 
            currentStep: 0, 
            hasCompleted: false,
            isFirstTime: true
          },
          studentSessionsTour: { 
            isActive: true, 
            currentStep: 0, 
            hasCompleted: false,
            isFirstTime: true
          },
          attestationTour: { 
            isActive: true, 
            currentStep: 0, 
            hasCompleted: false,
            isFirstTime: true
          },
          viewTour: { 
            isActive: true, 
            currentStep: 0, 
            hasCompleted: false,
            isFirstTime: true
          },
          basicTimetableTour: {
            isActive: true,
            currentStep: 0
          },
          navTour: {
            isActive: true,
            currentStep: 0
          }
        }))

    // console.logresetAllToursToFirstTime: Global tour activated, others reset')
        
        // Force a re-render by dispatching a custom event
        window.dispatchEvent(new CustomEvent('tour-reset-completed'))
      },
    }),
    {
      name: 'admin-store',
    }
  )
)

// Initialize the tour from server on app startup
initAdminTourFromServer()